import React, { useMemo } from 'react';
import styled from 'astroturf/react';
import AvatarBase from 'js/social/shared/Avatar';
import { Body } from 'js/design_system/Body';
import { FollowableUser, Tag } from 'js/social/shared/types';
import { Autocomplete } from 'js/social/shared/SocialEditor/Autocomplete';
import { TAG_MAX } from 'js/social/shared/SocialEditor/utils';

const Avatar = styled(AvatarBase)`
  --size: 40px;
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const Affiliation = styled(Body)`
  color: $ds-gray-dark;
`;

type Props = {
  anchorTop?: number;
  anchorLeft?: number;
  users: FollowableUser[];
  tags: Tag[];
  loading: boolean;
  activeIndex: number;
  onActiveIndexChange: (activeIndex: number) => void;
  onSelect: (tag: FollowableUser) => void;
};

export const TagSelector: React.FC<Props> = ({
  anchorTop = 0,
  anchorLeft = 0,
  users,
  tags,
  loading,
  activeIndex,
  onActiveIndexChange,
  onSelect,
}) => {
  const message = useMemo(() => {
    if (tags.length >= TAG_MAX) {
      return "You've reached the limit of people you can add.";
    } else if (users.length <= 0) {
      return 'No results found.';
    }
    return undefined;
  }, [tags, users]);

  return (
    <Autocomplete
      anchorTop={anchorTop}
      anchorLeft={anchorLeft}
      items={users}
      message={message}
      activeIndex={activeIndex}
      loading={loading}
      keyExtractor={(user) => user.id}
      renderItem={(user) => (
        <>
          <Avatar
            fullName={user.displayName}
            photoUrl={user.photo}
            profileUrl={user.url}
          />
          <Details>
            <Body size="md" bold>
              {user.displayName}
            </Body>
            <Affiliation size="sm">{user.university?.displayName}</Affiliation>
          </Details>
        </>
      )}
      onActiveIndexChange={onActiveIndexChange}
      onSelect={onSelect}
    />
  );
};
